import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Avatar,
  Switch,
  FormControlLabel,
  FormGroup,
  Divider,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment,
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import {
  PhotoCamera,
  Save,
  Person,
  Share,
  CheckCircle,
  Error,
  AccountBalance,
} from '@mui/icons-material';
import { useUser } from '../contexts/UserContext';
import { UserService } from '../services/userService';
import { UserProfile } from '../types';
import { collection, query, where, getDocs, limit } from 'firebase/firestore';
import { db, storage } from '../config/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import AlpacaConnectionStatus from './AlpacaConnectionStatus';

const ProfileSettings: React.FC = () => {
  const { user, profile, refreshProfile } = useUser();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [handleAvailable, setHandleAvailable] = useState<boolean | null>(null);
  const [checkingHandle, setCheckingHandle] = useState(false);
  
  // Form fields
  const [displayName, setDisplayName] = useState(profile?.displayName || '');
  const [handle, setHandle] = useState(profile?.handle || '');
  const [bio, setBio] = useState(profile?.bio || '');
  const [profilePicUrl, setProfilePicUrl] = useState(profile?.profilePicUrl || '');
  
  // Sharing preferences
  const [watchlistSharing, setWatchlistSharing] = useState(
    profile?.sharingPreferences?.watchlistSharing ?? true
  );
  const [defaultPortfolioSharing, setDefaultPortfolioSharing] = useState(
    profile?.sharingPreferences?.defaultPortfolioSharing ?? false
  );
  const [profileVisibility, setProfileVisibility] = useState(
    profile?.sharingPreferences?.profileVisibility ?? 'authenticated'
  );

  useEffect(() => {
    if (profile) {
      setDisplayName(profile.displayName || '');
      setHandle(profile.handle || '');
      setBio(profile.bio || '');
      setProfilePicUrl(profile.profilePicUrl || '');
      setWatchlistSharing(profile.sharingPreferences?.watchlistSharing ?? true);
      setDefaultPortfolioSharing(profile.sharingPreferences?.defaultPortfolioSharing ?? false);
      setProfileVisibility(profile.sharingPreferences?.profileVisibility ?? 'authenticated');
    }
  }, [profile]);

  const checkHandleAvailability = async (newHandle: string) => {
    if (!newHandle || newHandle === profile?.handle) {
      setHandleAvailable(null);
      return;
    }

    // Validate handle format
    if (!/^[a-zA-Z0-9_]{3,20}$/.test(newHandle)) {
      setHandleAvailable(false);
      return;
    }

    setCheckingHandle(true);
    try {
      const q = query(
        collection(db, 'userProfiles'),
        where('handle', '==', newHandle),
        limit(1)
      );
      const snapshot = await getDocs(q);
      setHandleAvailable(snapshot.empty);
    } catch (error) {
      console.error('Error checking handle availability:', error);
      setHandleAvailable(null);
    } finally {
      setCheckingHandle(false);
    }
  };

  const handleHandleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newHandle = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '');
    setHandle(newHandle);
    if (newHandle) {
      checkHandleAvailability(newHandle);
    } else {
      setHandleAvailable(null);
    }
  };

  const handleSave = async () => {
    if (!user || !profile) return;

    setSaving(true);
    setMessage(null);

    try {
      const updates: Partial<UserProfile> = {
        displayName,
        sharingPreferences: {
          watchlistSharing,
          defaultPortfolioSharing,
          profileVisibility: profileVisibility as 'public' | 'authenticated',
        },
      };
      
      // Only add optional fields if they have values
      if (handle) updates.handle = handle;
      if (bio) updates.bio = bio;
      if (profilePicUrl) updates.profilePicUrl = profilePicUrl;

      await UserService.updateUserProfile(user.uid, updates);
      
      // Trigger profile refresh to update context
      await refreshProfile();
      
      setMessage({ type: 'success', text: 'Profile updated successfully!' });
    } catch (error) {
      console.error('Error updating profile:', error);
      setMessage({ type: 'error', text: 'Failed to update profile. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const handleProfilePicUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || !event.target.files[0] || !user) return;
    
    const file = event.target.files[0];
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      setMessage({ type: 'error', text: 'Please select an image file' });
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'Image must be less than 5MB' });
      return;
    }
    
    setLoading(true);
    setMessage(null);
    
    try {
      // Create a reference to the file in Firebase Storage
      const timestamp = Date.now();
      const fileName = `profile-pics/${user.uid}_${timestamp}_${file.name}`;
      const storageRef = ref(storage, fileName);
      
      // Upload the file
      const snapshot = await uploadBytes(storageRef, file);
      
      // Get the download URL
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      // Update the profile picture URL in state
      setProfilePicUrl(downloadURL);
      
      setMessage({ 
        type: 'success', 
        text: 'Profile picture uploaded! Click "Save Changes" to update your profile.' 
      });
    } catch (error) {
      console.error('Error uploading profile picture:', error);
      setMessage({ 
        type: 'error', 
        text: 'Failed to upload profile picture. Please try again.' 
      });
    } finally {
      setLoading(false);
    }
  };

  if (!user || !profile) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Profile Settings
      </Typography>

      {message && (
        <Alert 
          severity={message.type} 
          onClose={() => setMessage(null)}
          sx={{ mb: 2 }}
        >
          {message.text}
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Person sx={{ mr: 1 }} />
          <Typography variant="h6">Basic Information</Typography>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: 'center' }}>
              <Avatar
                sx={{ width: 120, height: 120, mx: 'auto', mb: 2 }}
                src={profilePicUrl}
              >
                {displayName?.charAt(0).toUpperCase() || user.email?.charAt(0).toUpperCase()}
              </Avatar>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="profile-pic-upload"
                type="file"
                onChange={handleProfilePicUpload}
              />
              <label htmlFor="profile-pic-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<PhotoCamera />}
                  disabled={saving || loading}
                >
                  {loading ? 'Uploading...' : 'Upload Photo'}
                </Button>
              </label>
            </Box>
          </Grid>

          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              label="Display Name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              margin="normal"
              disabled={saving}
            />

            <TextField
              fullWidth
              label="Handle"
              value={handle}
              onChange={handleHandleChange}
              margin="normal"
              disabled={saving}
              InputProps={{
                startAdornment: <InputAdornment position="start">@</InputAdornment>,
                endAdornment: checkingHandle ? (
                  <CircularProgress size={20} />
                ) : handleAvailable === true ? (
                  <CheckCircle color="success" />
                ) : handleAvailable === false ? (
                  <Error color="error" />
                ) : null,
              }}
              helperText={
                handleAvailable === false
                  ? 'This handle is already taken'
                  : 'Letters, numbers, and underscores only (3-20 characters)'
              }
              error={handleAvailable === false}
            />

            <TextField
              fullWidth
              label="Bio"
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              margin="normal"
              multiline
              rows={3}
              disabled={saving}
              helperText="Tell others about your trading style and interests"
            />
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Share sx={{ mr: 1 }} />
          <Typography variant="h6">Sharing Preferences</Typography>
        </Box>

        <FormGroup>
          <FormControlLabel
            control={
              <Switch
                checked={watchlistSharing}
                onChange={(e) => setWatchlistSharing(e.target.checked)}
                disabled={saving}
              />
            }
            label="Share my watchlist"
            sx={{ mb: 1 }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, ml: 4 }}>
            Allow others to view and copy stocks from your watchlist
          </Typography>

          <FormControlLabel
            control={
              <Switch
                checked={defaultPortfolioSharing}
                onChange={(e) => setDefaultPortfolioSharing(e.target.checked)}
                disabled={saving}
              />
            }
            label="Share new portfolios by default"
            sx={{ mb: 1 }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, ml: 4 }}>
            New portfolios will be shared by default (you can change this per portfolio)
          </Typography>

          <FormControlLabel
            control={
              <Switch
                checked={profileVisibility === 'public'}
                onChange={(e) => setProfileVisibility(e.target.checked ? 'public' : 'authenticated')}
                disabled={saving}
              />
            }
            label="Make profile public"
            sx={{ mb: 1 }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
            {profileVisibility === 'public'
              ? 'Anyone can view your profile'
              : 'Only logged-in users can view your profile'}
          </Typography>
        </FormGroup>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || handleAvailable === false}
            startIcon={saving ? <CircularProgress size={20} /> : <Save />}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </Paper>

      {profile?.isAdmin && (
        <Paper sx={{ p: 3, mt: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <AccountBalance sx={{ mr: 1 }} />
            <Typography variant="h6">Alpaca Paper Trading</Typography>
          </Box>
          <AlpacaConnectionStatus />
        </Paper>
      )}
    </Box>
  );
};

export default ProfileSettings;